import { Box, Stack, Text } from '@mantine/core'
import { Fragment } from 'react'

import type { PredictionContract, PredictionEvent } from '@/services/prediction/types'

import { CompactCardFooter } from '../_shared/CompactCardFooter'
import { MarketCardHeader } from '../_shared/MarketCardHeader'
import { RecurringLiveSubtitle } from '../_shared/RecurringLiveSubtitle'
import { VolumeLabel } from '../_shared/VolumeLabel'
import { HydrationSafe } from '../hooks/HydrationSafe'
import { useGetTeamColor } from '../hooks/useGetTeamColor'
import { MAX_COMPACT_FUTURE_OUTCOMES, useLeagueOutcomes } from '../hooks/useLeagueOutcomes'
import { useRecurringEventLabel } from '../hooks/useRecurringEventLabel'
import { useScheduleLabel } from '../hooks/useScheduleLabel'
import { formatOddsMultiplier } from '../utils/formatOddsMultiplier'
import { formatPercentage } from '../utils/formatPercentage'
import { isRecurringEvent, usesBareIcon } from '../utils/recurring'
import { OutcomeIdentity } from './OutcomeRow'
import { resolveOutcomeVisuals } from './resolveOutcomeVisuals'
import { YesNoPriceCell } from './YesNoPriceCell'

const CELL_GAP = 4
/** Outcome 行高——对齐 TwoWayCard/SixBox 的 TEAM_ROW_HEIGHT（Figma）。 */
const OUTCOME_ROW_HEIGHT = 52
/** 倍数与 % 格间距（Figma 2466-105541：`1.73x` ·12px· `[54%]`）。 */
const MULTIPLIER_CELL_GAP = 12

interface FutureCardProps {
  event: PredictionEvent
  /** 页头图标兜底：section 已按 event_kind_group_id 解析好的组图标 URL。 */
  iconSrc?: string
  /** 页头 kind 名兜底：section 已按 event.event_kind 反查组 display_name。 */
  kindName?: string
  onClick?: () => void
  onContractClick?: (contract: PredictionContract) => void
}

/**
 * Port of cdc-web `Compact/FutureCard/index.tsx`，裁剪为 percentage-only
 * （Figma 2466-105541）：
 * - 本区无 odds 格式切换（决策 7e）→ 裁掉 American/decimal 分支、
 *   Yes/No 列头、useOddsFormat/useVisibleQuoteSides；
 * - 整卡跳转改为同源外链 `/explore/predict/markets/{slug}`（当前标签页跳转），
 *   由 section 在 .cardRoot 上包绝对定位 overlay 链接实现；卡片内部仍无路由，
 *   `usePredictPathPrefix` 未移植，CompactCardFooter 的 href 已降级为纯文本；
 * - recurring 卡（duration>0）页头 kind 名换周期标签（"2 hour"）、副标题换
 *   LIVE + 倒计时（`useRecurringEventLabel` + `RecurringLiveSubtitle`）、页头
 *   图标用 bare 变体（`usesBareIcon`）——对齐 cdc-web。
 *
 * 布局（纵向堆叠，同 TwoWayCard 解剖）：
 *   MarketCardHeader（图标位 + kind 名或周期标签 / 标题 / absolute 日期或 LIVE 倒计时）
 *   outcome grid（共享列：identity 列 minmax(0,1fr) + 价格列 max-content；
 *   每行 = OutcomeIdentity（40px logo + 名 + 胜率下划线）‖ 倍数 ‖ Yes/buy % 格）
 *   页脚（volume / parlay / "+N more outcomes"，countNoun=outcomes）
 */
export function FutureCard({
  event,
  iconSrc,
  kindName,
  onContractClick,
}: Readonly<FutureCardProps>) {
  const { displayContracts, remainingCount } = useLeagueOutcomes(event, MAX_COMPACT_FUTURE_OUTCOMES)

  const isRecurring = isRecurringEvent(event)
  const isSports = event.event_kind_asset_type === 'sports'
  const getTeamColor = useGetTeamColor(event)
  const { durationLabel, expiryCountdown, expiredAt } = useRecurringEventLabel(event)

  // League 卡永远显示日历日期（不显示 "Today"/星期，OG-8545）。
  const { label: scheduleLabel, datetime: scheduleDatetime } = useScheduleLabel(event, 'absolute')

  // recurring 卡副标题 = LIVE + 倒计时（cdc-web）；非 recurring = absolute 计划日期。
  const subtitle = isRecurring ? (
    <RecurringLiveSubtitle countdown={expiryCountdown} datetime={expiredAt} />
  ) : (
    <HydrationSafe>
      <Text
        variant="subtitle"
        c="var(--content-secondary)"
        component="time"
        dateTime={scheduleDatetime}
        suppressHydrationWarning
        truncate
      >
        {scheduleLabel}
      </Text>
    </HydrationSafe>
  )

  // 全卡最宽价格文本：喂给所有格做 ghost 宽度预留，跨行对齐一列宽。
  const sizingLabel = displayContracts.reduce((widest, c) => {
    const label = c.chance ? formatPercentage(c.yes) : null
    return label && label.length > widest.length ? label : widest
  }, '')

  return (
    <Box data-redesigned-card="true" style={{ position: 'relative', height: '100%' }}>
      {/* minWidth: 0 —— 窄卡（窗缩小时）下若缺，flex 子项按 max-content 撑宽
          卡片，outcome 行全文溢出绘制到倍数列/卡外（label 盖住 leverage）。 */}
      <Stack gap="sm" h="100%" style={{ minWidth: 0 }}>
        <MarketCardHeader
          iconSrc={iconSrc}
          kindName={
            isRecurring
              ? (durationLabel ?? (event.event_kind_display_name || kindName))
              : event.event_kind_display_name || kindName
          }
          title={event.title}
          subtitle={subtitle}
          iconVariant={usesBareIcon(event) ? 'bare' : 'boxed'}
        />

        {/* Outcome grid：identity 列 + 价格列（max-content，随最宽格定宽）。 */}
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            columnGap: CELL_GAP,
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {displayContracts.map((contract, rowIndex) => {
            const visuals = resolveOutcomeVisuals({
              contract,
              isRecurring,
              isSports,
              teamColor: getTeamColor(contract.team?.id, contract.id),
            })
            const gridRow = rowIndex + 1
            const chancePct = contract.chance ? Number(contract.chance) : null
            // 倍数算不出时显示 "—" 占位（formatOddsMultiplier 兜底），保持
            // 列布局跨行稳定（cdc-web OG-9666 PM 拍板）。
            const multiplier = formatOddsMultiplier(contract.chance)

            return (
              <Fragment key={contract.id}>
                <Box
                  style={{
                    gridColumn: 1,
                    gridRow,
                    minWidth: 0,
                    minHeight: OUTCOME_ROW_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <OutcomeIdentity
                    contract={contract}
                    visuals={visuals}
                    showUnderline
                    chancePct={chancePct}
                  />
                </Box>

                <Box
                  style={{
                    gridColumn: 2,
                    gridRow,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: MULTIPLIER_CELL_GAP,
                    flexShrink: 0,
                    minWidth: 'max-content',
                  }}
                >
                  <Text
                    variant="subtitle-emphasis"
                    c="var(--content-secondary)"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {multiplier}
                  </Text>
                  <Box style={{ marginLeft: 'auto' }}>
                    <YesNoPriceCell
                      contract={contract}
                      sizingLabel={sizingLabel}
                      onContractClick={onContractClick}
                    />
                  </Box>
                </Box>
              </Fragment>
            )
          })}
        </Box>

        {/* 页脚门控与 CompactCardFooter 自身可见性一致（同 TwoWayCard 注释）。 */}
        {(remainingCount > 0 || event.combos_enabled || event.cumulative_traded_usd) && (
          <Box data-card-footer style={{ marginTop: 'auto' }}>
            <CompactCardFooter
              moreCount={remainingCount}
              combosEnabled={event.combos_enabled}
              countNoun="outcomes"
              volumeSlot={<VolumeLabel volume={event.cumulative_traded_usd} />}
            />
          </Box>
        )}
      </Stack>
    </Box>
  )
}
